import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Building2, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/contexts/AuthContext';
import { inviteService } from '@/services/invite.service';
import { toast } from 'sonner';

export default function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const { user, isAuthenticated } = useAuth();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setError('Invalid invite link');
      setLoading(false);
      return;
    }

    // Load invite details
    const loadInvite = async () => {
      try {
        const inviteDetails = await inviteService.getInviteByToken(token);
        setInvite(inviteDetails);
        setLoading(false);
        
        // If not authenticated, redirect to signup/login
        if (!isAuthenticated) {
          localStorage.setItem('pendingInviteToken', token);
          navigate(`/signup?invite=${token}`);
        }
      } catch (err) {
        setError(err.response?.data?.detail || 'This invite is invalid or has expired');
        setLoading(false);
      }
    };
    
    loadInvite();
  }, [token, isAuthenticated, navigate]);

  const handleAccept = async () => {
    setAccepting(true);
    setError('');

    try {
      const result = await inviteService.acceptInvite(token);
      toast.success(`Welcome to ${result.organization?.name || invite.organization_name}!`);
      
      // Clear the initialized flag and reload to update auth context with new organization
      // The full page reload will re-initialize the auth context
      setTimeout(() => {
        window.location.href = '/';
      }, 500);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to accept invitation';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Accept invite error:', err.response?.data || err);
    } finally {
      setAccepting(false);
    }
  };

  const handleDecline = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center gradient-soft">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading invite...</p>
        </div>
      </div>
    );
  }

  if (error && !invite) {
    return (
      <div className="min-h-screen flex items-center justify-center gradient-soft p-4">
        <Card className="w-full max-w-md p-8 text-center shadow-strong">
          <XCircle className="h-16 w-16 text-destructive mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Invalid Invite</h2>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Button onClick={() => navigate('/login')} variant="outline">
            Go to Login
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center gradient-soft p-4">
      <Card className="w-full max-w-md p-8 shadow-strong">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full gradient-primary mb-4">
            <Building2 className="h-8 w-8 text-primary-foreground" />
          </div>
          <h1 className="text-3xl font-bold mb-2">You're Invited!</h1>
          <p className="text-muted-foreground">
            You've been invited to join an organization
          </p>
        </div>

        {error && (
          <Alert className="mb-6 border-destructive bg-destructive/10">
            <AlertDescription className="text-destructive">{error}</AlertDescription>
          </Alert>
        )}

        <div className="bg-muted/50 rounded-lg p-6 mb-6 space-y-3">
          <div>
            <p className="text-sm text-muted-foreground mb-1">Organization</p>
            <p className="text-lg font-semibold">{invite?.organization_name}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground mb-1">Your Role</p>
            <p className="text-lg font-semibold capitalize">
              {invite?.role.replace('_', ' ')}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground mb-1">Invited to</p>
            <p className="text-base">{invite?.email}</p>
          </div>
        </div>

        <div className="space-y-3">
          <Button 
            onClick={handleAccept} 
            className="w-full gradient-primary h-12"
            disabled={accepting}
          >
            {accepting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Accepting...
              </>
            ) : (
              <>
                <CheckCircle2 className="h-5 w-5" />
                Accept Invitation
              </>
            )}
          </Button>
          
          <Button 
            onClick={handleDecline} 
            variant="outline" 
            className="w-full"
            disabled={accepting}
          >
            Decline
          </Button>
        </div>
      </Card>
    </div>
  );
}